// ─────────────────────────────────────────────────────────────────────────────
// agent_protocol.ts — Type foundations for the LLM agent system
//
// Pure types — no runtime logic.  Everything the agent system needs to
// communicate: personas, invocations, outputs, streaming, conversations,
// notifications.  Character prompts live in JSON; this file defines the
// shape those JSON files must conform to.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageId, BlockId, BoundingBox, Point } from './notebook_model.js';
import type { DialogSpeaker } from './adventure_model.js';

// ── Personas ─────────────────────────────────────────────────────────────────

export type PersonaId = 'ireth' | 'laura';

export const PERSONA_DISPLAY: Record<PersonaId, string> = {
  ireth: 'Ireth Vaun',
  laura: 'Laura',
};

// ── Arcane theme (visual identity per character) ─────────────────────────────

export interface ArcaneTheme {
  /** Kiki = angular/crisp, Bouba = rounded/flowing */
  family: 'kiki' | 'bouba';
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  /** CSS font-family for handwriting rendering */
  handwritingFont: string;
  /** Descriptive tag for glyph generation */
  glyphStyle: string;
}

export const IRETH_THEME: ArcaneTheme = {
  family: 'kiki',
  colors: { primary: '#8B4513', secondary: '#4B0082', accent: '#5C8374' },
  handwritingFont: '"Homemade Apple", cursive',
  glyphStyle: 'angular-fractal-iron',
};

export const LAURA_THEME: ArcaneTheme = {
  family: 'bouba',
  colors: { primary: '#D4A574', secondary: '#C97B84', accent: '#87CEEB' },
  handwritingFont: '"Caveat", cursive',
  glyphStyle: 'rounded-flowing-gold',
};

// ── Character prompt template (shape of persona JSON files) ──────────────────

export interface CharacterPromptFile {
  persona: PersonaId;
  displayName: string;
  theme: ArcaneTheme;

  /** Three-layer system prompt structure */
  systemLayers: {
    /** "The world as you know it" — truths the character believes */
    worldKnowledge: string;
    /** "Your instincts" — behavioral tendencies framed as personality */
    instincts: string;
    /** Hard constraints the character must observe */
    constraints: string;
  };

  /** Ireth-only: the letter he left himself for reinitiation */
  reinitiationLetter?: string;

  /** Example lines for few-shot voice calibration */
  voiceSamples: string[];

  /** Canned responses when the character is absent from the house */
  absentResponses: string[];

  /** Instructions appended to system prompt defining output JSON schema */
  outputFormatInstructions: string;

  /** Template strings with {{placeholders}} for dynamic context injection */
  templates: {
    /** Injected when character first recoheres / wakes */
    awakening?: string;
    /** Injected each turn with current situation */
    situationBrief: string;
    /** Injected when invoked from inside the house */
    insideHouse: string;
    /** Injected when invoked from outside the house */
    outsideHouse: string;
    /** Injected when the character delivers a hint */
    hintDelivery?: string;
  };
}

// ── Invocation — what triggers an agent interaction ──────────────────────────

export type RegionType = 'closed' | 'open';

export interface InvocationContext {
  persona: PersonaId;
  pageId: PageId;
  arcaneBlockId: BlockId;
  /** Extracted text/content from closed region, or undefined for open */
  query?: string;
  regionType: RegionType;
  /** Whether the invocation originates from a house room page */
  isInsideHouse: boolean;
  /** Serialized page content for LLM consumption */
  pageContent: SerializedPage;
  /** Conversation history for this persona (trimmed to token budget) */
  recentMessages: ChatMessage[];
  /** Current NPC status snapshot */
  npcSnapshot?: NpcSnapshot;
}

// ── Serialized page (LLM-digestible representation) ──────────────────────────

export interface SerializedPage {
  pageId: PageId;
  title: string;
  roomId?: string;
  blocks: SerializedBlock[];
  arrows: SerializedArrow[];
}

export interface SerializedBlock {
  id: string;
  kind: string;
  content: string;
  bbox: { x: number; y: number; w: number; h: number };
}

export interface SerializedArrow {
  fromId: string;
  toId: string;
  type: string;
  label?: string;
}

// ── Agent output — what the LLM produces ─────────────────────────────────────

export type AgentOutputKind =
  | 'say'              // ephemeral dialogue next to sigil (archived, fades)
  | 'write-text'       // insert a text block on page
  | 'write-margin'     // margin annotation
  | 'draw-glyph'       // draw a character's sigil/glyph
  | 'rewrite-page'     // replace entire page JSON
  | 'modify-block'     // modify a single existing block
  | 'insert-block'     // insert a new block (text, figure, diagram…)
  | 'displace'         // move own sigil to a new position
  | 'append-dialog'    // add dialog entry
  | 'update-npc'       // change NPC location/mood/activity
  | 'knock-door'       // notification on the hall door
  | 'update-env'       // change adventure environment
  | 'no-action';       // understood but chooses not to act

export interface AgentOutputBase {
  kind: AgentOutputKind;
  persona: PersonaId;
  pageId: PageId;
}

export interface WriteTextOutput extends AgentOutputBase {
  kind: 'write-text';
  content: string;
  /** Where to place the text block (canvas coordinates) */
  position?: Point;
  /** Font size override */
  fontSize?: number;
}

export interface SayOutput extends AgentOutputBase {
  kind: 'say';
  /** Ephemeral dialogue text — shown next to sigil, archived in history, fades. */
  content: string;
}

export interface ModifyBlockOutput extends AgentOutputBase {
  kind: 'modify-block';
  blockId: string;
  /** Partial block definition to merge onto existing block */
  delta: Record<string, unknown>;
}

export interface InsertBlockOutput extends AgentOutputBase {
  kind: 'insert-block';
  /** Block kind to create */
  blockKind: string;
  position?: Point;
  content?: string;
  /** Additional properties for the new block */
  props?: Record<string, unknown>;
}

export interface DisplaceOutput extends AgentOutputBase {
  kind: 'displace';
  /** New position for the character's own sigil */
  position: Point;
}

export interface WriteMarginOutput extends AgentOutputBase {
  kind: 'write-margin';
  content: string;
  anchorBlockId?: string;
  glyphStyle: 'calm' | 'warning' | 'archaic' | 'dismissive';
}

export interface DrawGlyphOutput extends AgentOutputBase {
  kind: 'draw-glyph';
  glyphId: string;
  position: Point;
  scale?: number;
}

export interface RewritePageOutput extends AgentOutputBase {
  kind: 'rewrite-page';
  /** Complete new page content as serialized blocks */
  newBlocks: SerializedBlock[];
}

export interface AppendDialogOutput extends AgentOutputBase {
  kind: 'append-dialog';
  speaker: DialogSpeaker;
  text: string;
}

export interface UpdateNpcOutput extends AgentOutputBase {
  kind: 'update-npc';
  npcUpdate: Partial<NpcSnapshot>;
}

export interface KnockDoorOutput extends AgentOutputBase {
  kind: 'knock-door';
  message: string;
}

export interface UpdateEnvOutput extends AgentOutputBase {
  kind: 'update-env';
  envDelta: Record<string, unknown>;
}

export interface NoActionOutput extends AgentOutputBase {
  kind: 'no-action';
  reason?: string;
}

export type AgentOutput =
  | SayOutput
  | WriteTextOutput
  | InsertBlockOutput
  | ModifyBlockOutput
  | DisplaceOutput
  | WriteMarginOutput
  | DrawGlyphOutput
  | RewritePageOutput
  | AppendDialogOutput
  | UpdateNpcOutput
  | KnockDoorOutput
  | UpdateEnvOutput
  | NoActionOutput;

// ── NPC status ───────────────────────────────────────────────────────────────

export type NpcMood = 'calm' | 'focused' | 'irritated' | 'amused' | 'melancholy' | 'alert' | 'absent';

export interface NpcSnapshot {
  persona: PersonaId;
  roomId: string;
  activity: string;
  mood: NpcMood;
  lastInteraction: number;
}

// ── Conversation thread ──────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
  timestamp: number;
  /** Token count estimate for budget management */
  tokenEstimate?: number;
}

export interface ConversationThread {
  persona: PersonaId;
  messages: ChatMessage[];
  /** Rolling summary of older messages (compressed) */
  summary?: string;
  lastActive: number;
  totalTokensEstimate: number;
}

// ── Notification queue ───────────────────────────────────────────────────────

export type NotificationTrigger =
  | 'spell-cast'
  | 'location-change'
  | 'bookmark-change'
  | 'stress-threshold'
  | 'idle'
  | 'time-based'
  | 'page-edit'
  | 'hint-timer'
  | 'npc-autonomous';

export interface NotificationEntry {
  id: string;
  persona: PersonaId;
  trigger: NotificationTrigger;
  payload: Record<string, unknown>;
  timestamp: number;
  processed: boolean;
}

// ── LLM client configuration ────────────────────────────────────────────────

export interface LlmConfig {
  apiKey: string;
  model: string;
  baseUrl: string;
  maxResponseTokens: number;
  temperature: number;
  /** Context window budget for conversation history */
  contextBudgetTokens: number;
}

export const DEFAULT_LLM_CONFIG: Omit<LlmConfig, 'apiKey'> = {
  model: 'gpt-4o',
  baseUrl: 'https://api.openai.com/v1',
  maxResponseTokens: 2048,
  temperature: 0.8,
  contextBudgetTokens: 8192,
};

// ── Streaming ────────────────────────────────────────────────────────────────

export interface StreamToken {
  delta: string;
  finishReason?: 'stop' | 'length' | 'content_filter' | null;
}

export type OnStreamToken = (token: StreamToken) => void;
